/**
 * Pool of terrain/grass workers. Idle workers pull queued patches; cancellation
 * removes obsolete waiting work and discards already executing results. Completed
 * patches resolve under the frame's CPU and deferred-upload budgets.
 */
import type { CanyonCliffSolid, CellId, PlanetDef } from "@hyperlight/shared";
import type { PatchRequest, PatchData, RegionBuildData, RegionBuildRequest, RegionsMsg } from "./patch";
import { FrameQueue } from '../engine/frameQueue';

interface PatchJob {
  request: PatchRequest;
  resolve: (data: PatchData | null) => void;
  detach: () => void;
}
export interface CanyonCliffRequest {kind:'canyonCliffs';id:number;planet:PlanetDef}
export interface CanyonCliffData {kind:'canyonCliffsBuilt';id:number;solids:readonly CanyonCliffSolid[]|null;buildMs:number;error?:string}
interface CanyonCliffJob {request:CanyonCliffRequest;resolve:(solids:readonly CanyonCliffSolid[]|null)=>void;detach:()=>void}

/** CanyonCliffMeshes expands source geometry into 24 unique Float32 attribute
 * values per vertex and Uint32 indices. Its three scalar zero attributes share
 * one BufferAttribute, so count that backing buffer only once. */
export function canyonCliffUploadBytes(data:CanyonCliffData):number {
  return data.solids?.reduce((bytes,solid)=>bytes+(solid.positions.length/3)*24*Float32Array.BYTES_PER_ELEMENT+solid.indices.length*Uint32Array.BYTES_PER_ELEMENT,0)??0;
}

/** CPU adoption is cheap after offloading grass, but its GPU upload is not free. */
export function patchUploadBytes(data: PatchData): number {
  let bytes = 0;
  for (const value of Object.values(data)) if (ArrayBuffer.isView(value)) bytes += value.byteLength;
  for (const field of data.grass?.fields ?? []) bytes += field.roots.byteLength + field.data.byteLength + field.tints.byteLength;
  // The terrain material packs road distance and snow retention into one vec2.
  // Warm worker packets omit retention, but mounting still allocates its lane.
  if (!data.snowRetention) bytes += data.roadSd?.byteLength ?? 0;
  // Instance matrices + conservative color/contact overhead after mounting.
  bytes += (data.wetland?.length ?? 0) * 112;
  bytes += (data.coastal?.length ?? 0) * 112;
  bytes += (data.savanna?.length ?? 0) * 112;
  bytes += (data.inlandWetland?.length ?? 0) * 112;
  bytes += (data.riparian?.length ?? 0) * 112;
  // Mounted water has 12 unique float values per vertex, plus its indices.
  for(const chunk of data.inlandWater?.chunks??[])bytes+=chunk.depth.length*12*4+chunk.indices.byteLength;
  const pads=data.inlandWater?.pads;
  if(pads)bytes+=pads.positions.byteLength+pads.normals.byteLength+pads.colors.byteLength+pads.indices.byteLength;
  return bytes;
}

export class TerrainWorkerPool {
  private workers: Worker[] = [];
  private next = 0;
  private seq = 1;
  private pending = new Map<number, PatchJob>();
  private readonly waiting: number[] = [];
  private readonly busy = new Set<Worker>();
  private closed = false;
  private pendingRegions = new Map<number, (d: ArrayBuffer[]) => void>();
  private readonly pendingCliffs=new Map<number,CanyonCliffJob>();
  private readonly completedCliffs=new FrameQueue<CanyonCliffData>(data=>{
    const job=this.pendingCliffs.get(data.id);if(job){this.pendingCliffs.delete(data.id);job.detach();job.resolve(data.solids);}
  }).withWorkBudget(canyonCliffUploadBytes,256*1024);
  // Keep completed patches counted in pending until adopted. MAX_QUEUE then
  // bounds both worker work and the main-thread backlog while moving/loading.
  private readonly completed = new FrameQueue<PatchData>((data) => {
    const job = this.pending.get(data.id);
    if (job) { this.pending.delete(data.id); job.detach(); job.resolve(data); }
  }).withWorkBudget(patchUploadBytes, 1024 * 1024);

  constructor(size = Math.min(4, Math.max(2, (navigator.hardwareConcurrency || 4) - 2))) {
    for (let i = 0; i < size; i++) {
      const w = new Worker(new URL("./terrain.worker.ts", import.meta.url), { type: "module" });
      w.onmessage = (ev: MessageEvent<PatchData | RegionBuildData | CanyonCliffData>) => {
        if((ev.data as CanyonCliffData).kind==='canyonCliffsBuilt'){
          const data=ev.data as CanyonCliffData;this.busy.delete(w);
          if(this.pendingCliffs.has(data.id)){if(data.error)console.error('Canyon cliff worker:',data.error);this.completedCliffs.push(data);}
          this.dispatch();return;
        }
        if ((ev.data as RegionBuildData).kind === "regionBuilt") {
          const data = ev.data as RegionBuildData;
          const resolve = this.pendingRegions.get(data.id);
          if (resolve) { this.pendingRegions.delete(data.id); resolve(data.buffers); }
          return;
        }
        const data = ev.data as PatchData;
        this.busy.delete(w);
        if (this.pending.has(data.id)) this.completed.push(data);
        this.dispatch();
      };
      this.workers.push(w);
    }
  }

  get queueDepth(): number {
    return this.pending.size + this.pendingRegions.size + this.pendingCliffs.size;
  }

  /** At most one patch executing per worker; obsolete waiting jobs never run. */
  build(req: Omit<PatchRequest, "id">, signal?: AbortSignal): Promise<PatchData | null> {
    if (this.closed || signal?.aborted) return Promise.resolve(null);
    const id = this.seq++;
    return new Promise<PatchData | null>((resolve) => {
      const abort = () => {
        if (!this.pending.delete(id)) return;
        signal?.removeEventListener('abort', abort);
        const index = this.waiting.indexOf(id);
        if (index >= 0) this.waiting.splice(index, 1);
        resolve(null);
      };
      this.pending.set(id, { request: { ...req, id }, resolve,
        detach: () => signal?.removeEventListener('abort', abort) });
      signal?.addEventListener('abort', abort, { once: true });
      this.waiting.push(id);
      this.dispatch();
    });
  }

  private dispatch(): void {
    if (this.closed) return;
    for (const worker of this.workers) {
      if (this.busy.has(worker)) continue;
      let job: PatchJob | CanyonCliffJob | undefined;
      while (!job && this.waiting.length) {const id=this.waiting.shift()!;job=this.pending.get(id)??this.pendingCliffs.get(id);}
      if (!job) break;
      this.busy.add(worker);
      worker.postMessage(job.request);
    }
  }

  /** Bounded finite geography, prioritized ahead of queued detail patches.
   * Results resolve under a frame budget; the caller publishes physics and
   * rendering together. Null means cancelled, disposed, failed or saturated. */
  buildCanyonCliffs(planet:PlanetDef,signal?:AbortSignal):Promise<readonly CanyonCliffSolid[]|null>{
    if(this.closed||signal?.aborted||this.pendingCliffs.size>=8)return Promise.resolve(null);
    const id=this.seq++;
    return new Promise(resolve=>{
      const abort=()=>{if(!this.pendingCliffs.delete(id))return;signal?.removeEventListener('abort',abort);const index=this.waiting.indexOf(id);if(index>=0)this.waiting.splice(index,1);resolve(null);};
      this.pendingCliffs.set(id,{request:{kind:'canyonCliffs',id,planet},resolve,detach:()=>signal?.removeEventListener('abort',abort)});
      signal?.addEventListener('abort',abort,{once:true});this.waiting.unshift(id);this.dispatch();
    });
  }

  /** Grow expensive CityGraphs on a terrain worker, returning serialized buffers. */
  buildRegions(planet: PlanetDef, cells: CellId[]): Promise<ArrayBuffer[]> {
    const id = this.seq++;
    return new Promise<ArrayBuffer[]>((resolve) => {
      this.pendingRegions.set(id, resolve);
      const worker = this.workers[this.next];
      this.next = (this.next + 1) % this.workers.length;
      const request: RegionBuildRequest = { kind: "regionBuild", id, planet, cells };
      worker.postMessage(request);
    });
  }

  /**
   * Broadcast a region-buffer message to EVERY worker so each rebuilds its own
   * local settlement index (W2b-2 Task 1). Deliberately NO transfer list: a
   * transferable moves ownership, so a single buffer cannot be transferred to
   * more than one worker — structured clone instead hands each worker an
   * independent copy of every buffer.
   */
  postAll(msg: RegionsMsg): void {
    for (const w of this.workers) w.postMessage(msg);
  }

  dispose(): void {
    this.closed = true;
    this.completed.dispose();
    this.completedCliffs.dispose();
    this.waiting.length = 0;
    this.busy.clear();
    for (const job of this.pending.values()) { job.detach(); job.resolve(null); }
    for (const job of this.pendingCliffs.values()) { job.detach(); job.resolve(null); }
    for (const w of this.workers) w.terminate();
    this.workers = [];
    this.pending.clear();
    this.pendingCliffs.clear();
    this.pendingRegions.clear();
  }
}
